// UT Matrix Comparison Report List API
import { CommonRequest, Method, ServiceName, CommonResponse } from 'models/common/RestApi';
import {
  AtcDivsCd,
  ComparisonReportSearchParam,
  RptOpinion,
  SaveReportResult,
  UtMatrixComparisonReportList,
} from 'models/ut/UtMatrixComparisonReportList';
import { UT_DB_CODE, UT_TYPES, findUtType, utUnitOf } from 'pages/ut/constants';
import { ElecSourceRow } from 'pages/ut/elec/types';
import { buildElecAnalysisTexts } from 'pages/ut/elec/utils/compareElec';
import { CompareTarget } from 'pages/ut/popup/UtMatrixCompareTargetPopup';
import { AnalysisRow, AtcDivsCdSum, MstTgtTpCd, RptSumItem, RptUtmTarget } from 'pages/ut/types';
import { buildAllMechUtDiffTexts } from 'pages/ut/utils/mechUtDiff';
import { callApi } from 'utils/ApiUtil';

export const getUtMatrixComparisonReportList = async (searchParam: ComparisonReportSearchParam) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/ut/getUtMatrixComparisonReportList`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ ...searchParam }),
  };
  const response: CommonResponse<UtMatrixComparisonReportList[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : null) as UtMatrixComparisonReportList[];
};

/**
 * 저장 응답을 SaveReportResult로 정규화.
 * RPT_ID 문자열만 반환하던 구버전 응답과, 중복 여부를 함께 주는 신버전 응답을 모두 받는다.
 */
export const toSaveReportResult = (data: any): SaveReportResult => {
  if (typeof data === 'string') return { rptId: data, duplicated: false };
  return {
    rptId: String(data?.rptId ?? ''),
    duplicated: data?.duplicated === true || data?.dupYn === 'Y',
    rptNm: data?.rptNm ? String(data.rptNm) : undefined,
  };
};

export interface SaveComparisonReportReq {
  rptNm: string;
  userId: string;
  /** 2~3건, 배열 순서 = SORT_ORD. 3번(참조)은 대상 목록에만 저장된다 */
  selectedMatrices: CompareTarget[];
  /** TB_EELMB_UTM_RPT_D — 기준↔비교대상 */
  machDiffRows?: any[];
  /** TB_EELMB_UTM_RPT_ELPW_D — 기준↔비교대상 */
  elecDiffRows?: any[];
}

/**
 * 기계/전기 통합 리포트 생성 — RPT_M 1건 + RPT_UTM_M 2~3건 + RPT_D + RPT_ELPW_D를 한 번에 저장
 * 동일 대상·동일 순서 리포트가 이미 있으면 BE가 생성하지 않고 기존 RPT_ID를 duplicated와 함께 돌려준다.
 */
export const saveComparisonReport = async (
  req: SaveComparisonReportReq
): Promise<SaveReportResult> => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/ut/saveComparisonReport`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: { ...req },
  };
  const response: CommonResponse<any> = await callApi(request);
  if (response.successOrNot !== 'Y') throw new Error(String(response.data ?? '리포트 생성 실패'));
  const result = toSaveReportResult(response.data);
  if (!result.rptId) throw new Error('리포트 생성 실패 — RPT_ID가 반환되지 않았습니다.');
  return result;
};

/**
 * BE 응답 1행 → RptOpinion 정규화.
 *
 * 신 스키마(ATC_DIVS_CD + ANLS_OPIN_TP_CD)로 BE가 교체되기 전까지는 구 응답
 * (UTM_TP_CD 기준)이 내려올 수 있으므로, 없는 필드는 빈 값으로 채워 화면이 깨지지
 * 않게 한다. 구 응답에는 항목 구분이 없어 어느 도메인 행인지 알 수 없으므로,
 * 화면은 공통코드 목록을 기준으로 렌더하고 내용만 이 결과에서 채운다.
 */
const toRptOpinion = (raw: any): RptOpinion => ({
  rptId: raw?.rptId ? String(raw.rptId) : undefined,
  utmId: raw?.utmId ? String(raw.utmId) : undefined,
  sortOrd: Number(raw?.sortOrd ?? 0),
  atcDivsCd: raw?.atcDivsCd === 'ELEC' ? 'ELEC' : 'MECH',
  // 구 응답 호환 — 신 필드가 없으면 UTM_TP_CD를 항목키로 쓴다
  anlsOpinTpCd: String(raw?.anlsOpinTpCd ?? raw?.utmTpCd ?? ''),
  anlsOpinTpNm: raw?.anlsOpinTpNm ?? raw?.utmTpNm ?? undefined,
  anlsRltCtn: String(raw?.anlsRltCtn ?? ''),
  aiAnsCtn: String(raw?.aiAnsCtn ?? ''),
  opinCtn: String(raw?.opinCtn ?? ''),
});

/**
 * 전기 분석 결과 > 차단기 여유율 분석
 * @param rptId
 */
export const getBreakerMarginRatioList = async (rptId: string): Promise<any[]> => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/ut/getBreakerMarginRatioList`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ rptId }),
  };
  const response: CommonResponse<any[]> = await callApi(request);
  if (response.successOrNot !== 'Y') throw new Error('분석결과/의견 조회 실패');
  return response.data ?? [];
};

/**
 * 전기 분석 결과 > Machine 별 차단기 여유율
 * @param rptId
 */
export const getMachineMarginRatioList = async (rptId: string): Promise<any[]> => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/ut/getMachineMarginRatioList`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ rptId }),
  };
  const response: CommonResponse<any[]> = await callApi(request);
  if (response.successOrNot !== 'Y') throw new Error('분석결과/의견 조회 실패');
  return response.data ?? [];
};

/** 분석결과/의견 조회 (TB_EELMB_UTM_RPT_OPIN_M) — 기계/전기 전체를 반환한다 */
export const getRptOpinions = async (rptId: string): Promise<RptOpinion[]> => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/ut/getRptOpinions`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ rptId }),
  };
  const response: CommonResponse<any[]> = await callApi(request);
  if (response.successOrNot !== 'Y') throw new Error('분석결과/의견 조회 실패');
  return (response.data ?? []).map(toRptOpinion);
};

/** UT 항목별 분석결과/의견 저장 — 전체 목록을 덮어쓰는 방식 */
export const saveRptOpinions = async (
  rptId: string,
  opinions: RptOpinion[],
  userId: string
): Promise<void> => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/ut/saveRptOpinions`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: { rptId, userId, opinions },
  };
  const response: CommonResponse<any> = await callApi(request);
  if (response.successOrNot !== 'Y')
    throw new Error(String(response.data ?? '분석결과/의견 저장 실패'));
};

/**
 * 정합화 Report AI 전송 — 2개 UT Matrix 데이터를 AI 시스템에 전송
 * AI 응답은 TB_EELMB_UTM_RPT_OPIN_M.AI_ANS_CTN에 저장된다.
 */
export const sendMatrixReport = async (
  utmId: string,
  reportDomain: string,
  reportId: string,
  reportName: string
): Promise<string> => {
  // ⚠ ApiUtil은 queryParams를 GET/DELETE에만 붙인다(POST/PUT/PATCH는 config에 params가 없음).
  //   그래서 POST는 반드시 bodyParams로 보내야 한다 — BE도 @RequestBody로 받아야 한다.
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/ut/ai-ref/report`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: {
      utmId: utmId,
      reportDomain: reportDomain,
      reportId: reportId,
      reportName: reportName,
    },
    // AI 응답이 약 180초 — 기본 120초로는 ECONNABORTED가 난다.
    // BE가 비동기 접수로 바뀌면(v2 §2) 이 값은 다시 낮출 수 있다.
    timeout: 240000,
  };
  const response: CommonResponse<string> = await callApi(request);
  if (response.successOrNot !== 'Y')
    throw new Error(String(response.data ?? 'AI 리포트 전송 실패'));
  return response.data ?? '';
};

/** saveRuleBasedOpinions 결과 — 호출부가 사용자에게 알릴 수 있도록 실패 사유를 돌려준다 */
export interface RuleBasedSaveResult {
  ok: boolean;
  /** 전기 항목 건수 (A-0001/A-0002) */
  elecCount: number;
  /** 기계 UT 항목 건수 (9종) */
  mechCount: number;
  error?: string;
}

/**
 * 규칙 기반 자동 생성 항목 저장 — **AI가 아니라 규칙으로 만든다.**
 *
 * - 전기 종합분석 A-0001(차단기 여유율) / A-0002(Area별 전압 통합화 제안)
 *   → elec/utils/compareElec.ts의 buildElecAnalysisTexts
 * - 기계 UT 항목별 분석결과(⑥) 9종 → utils/mechUtDiff.ts의 buildAllMechUtDiffTexts
 *
 * 리포트 생성 직후 한 번 호출한다. 전기 입력은 기준(MST) UT Matrix의 전기 원천 행 전체,
 * 기계 입력은 비교 분석 결과(AnalysisRow[])다.
 *
 * ⚠ **saveRptOpinions 호출은 반드시 한 번이어야 한다.** 이 API는 의견 행을 통째로
 *   덮어쓰기 때문에, 전기·기계를 따로 저장하면 뒤에 실행되는 쪽이 앞서 넣은 행을 DB
 *   재조회로 되살려야 한다. 그 재조회가 실패하거나(아래 .catch → []) BE 응답에 행이
 *   빠지면 앞서 넣은 행이 조용히 사라진다 — 실제로 전기 2항목이 이렇게 유실됐다.
 *
 * ⚠ A-0003(훅업 포인트 간소화)은 **BE가 saveComparisonReport 트랜잭션 안에서 INSERT한다.**
 *   여기서 만들지 않고, 조회한 행을 keep으로 그대로 되돌려 보내 보존한다.
 *   → pages/ut/ref/BE-REQUEST-elec-hookup-simplify.md
 *
 * 저장 실패가 리포트 생성 자체를 실패로 만들면 안 되므로 예외는 삼키되, **결과를 돌려줘
 * 호출부가 사용자에게 알릴 수 있게 한다** — 조용히 비어 있으면 원인을 찾을 수 없다.
 */
export const saveRuleBasedOpinions = async (
  rptId: string,
  baseElecRows: ElecSourceRow[],
  machRows: AnalysisRow[],
  tgtUtmId: string,
  userId: string
): Promise<RuleBasedSaveResult> => {
  const empty = { elecCount: 0, mechCount: 0 };
  if (!rptId || !userId) {
    // 예전에는 로그 없이 return이라 아무 흔적도 남지 않았다
    const error = `저장 조건 미충족 (rptId=${rptId || '없음'}, userId=${userId || '없음'})`;
    console.error(`[규칙기반 분석] ${error}`);
    return { ok: false, ...empty, error };
  }
  try {
    const elecTexts = buildElecAnalysisTexts(baseElecRows ?? []);
    const mechTexts = machRows?.length ? buildAllMechUtDiffTexts(machRows) : null;
    const existing = await getRptOpinions(rptId).catch(() => [] as RptOpinion[]);

    const prevElec = new Map(
      existing.filter((o) => o.atcDivsCd === 'ELEC').map((o) => [o.anlsOpinTpCd, o])
    );
    // 항목키가 화면키('Steam')로 저장된 행과 DB코드('ST')로 저장된 행이 섞일 수 있어
    // findUtType()으로 정규화해서 대조한다 (constants.ts UT_DB_CODE 주석 참고)
    const prevMech = new Map(
      existing
        .filter((o) => o.atcDivsCd === 'MECH')
        .map((o) => [findUtType(o.anlsOpinTpCd)?.key, o] as const)
        .filter(([k]) => !!k)
    );

    const elecRows: RptOpinion[] = elecTexts.map((t) => {
      const prev = prevElec.get(t.anlsOpinTpCd);
      return {
        rptId,
        utmId: tgtUtmId,
        sortOrd: t.sortOrd,
        atcDivsCd: 'ELEC',
        anlsOpinTpCd: t.anlsOpinTpCd,
        // 규칙 기반 생성문은 ANLS_RLT_CTN에 넣는다 — 기계 UT 항목별 분석결과(⑥)와 같은 자리.
        // AI_ANS_CTN은 AI가 쓰는 칸이라 전기는 건드리지 않고 기존 값을 그대로 보존한다.
        anlsRltCtn: t.text,
        aiAnsCtn: prev?.aiAnsCtn ?? '',
        opinCtn: prev?.opinCtn ?? '', // 사용자가 써둔 검토 의견 보존
      };
    });

    const mechUtRows: RptOpinion[] = mechTexts
      ? UT_TYPES.map((ut, idx) => {
          const prev = prevMech.get(ut.key);
          return {
            rptId,
            utmId: tgtUtmId,
            sortOrd: idx + 1,
            atcDivsCd: 'MECH' as const,
            anlsOpinTpCd: UT_DB_CODE[ut.key],
            anlsRltCtn: mechTexts[ut.key] ?? '',
            aiAnsCtn: prev?.aiAnsCtn ?? '',
            opinCtn: prev?.opinCtn ?? '', // 사용자가 써둔 검토 의견 보존
          };
        })
      : [];

    // 이번에 새로 만드는 자리만 비우고 나머지는 그대로 되돌린다 —
    // 전기 A-0003, 기계 AI 분석 항목(A-1001/A-1002), 사용자 의견이 여기에 실려 보존된다.
    const elecCodes = new Set(elecTexts.map((t) => t.anlsOpinTpCd));
    const keep = existing.filter((o) =>
      o.atcDivsCd === 'ELEC'
        ? !elecCodes.has(o.anlsOpinTpCd)
        : !(mechUtRows.length > 0 && !!findUtType(o.anlsOpinTpCd))
    );

    await saveRptOpinions(rptId, [...keep, ...elecRows, ...mechUtRows], userId);
    console.log(
      `[규칙기반 분석] 저장 완료 — 전기 ${elecRows.length}건 / 기계 ${mechUtRows.length}건 (rptId=${rptId})`
    );
    return { ok: true, elecCount: elecRows.length, mechCount: mechUtRows.length };
  } catch (e) {
    const error = e instanceof Error ? e.message : String(e);
    console.error(`[규칙기반 분석] 저장 실패 (rptId=${rptId})`, e);
    return { ok: false, ...empty, error };
  }
};

// ─── 최종 리포트 AI 호출 (/v1/ut/ai-ref/report/final) ────────────────────

/** ai_content.{mech|elec}.sections[] 1건 */
export interface FinalReportSection {
  id: string;
  title: string;
  body: string;
}

export interface SendFinalReportReq {
  /** 인터페이스 ID — BE가 채번한다면 비워 보낸다 */
  ifId?: string;
  reportId: string;
  reportNm: string;
  /** 대상 UT Matrix ID — 기준/비교대상/참조 */
  baseUtmId: string;
  comparedUtmId: string;
  referUtmId?: string;
  /** 사용자가 입력한 최종 의견 (OPIN_CTN) */
  userContent: { mech: string; elec: string };
  /** AI가 만든 본문 (AI_ANS_CTN) */
  aiContent: { mech: FinalReportSection[]; elec: FinalReportSection[] };
}

/**
 * 최종 리포트 AI 호출 — 재저장 시점에 지금까지의 사용자 의견 + AI 본문을 함께 보낸다.
 *
 * 요청 형식은 ref/html/ut_matrix_rpt_demo.html 219~311줄 스펙 그대로이며,
 * 바디 키가 snake_case라 여기서 변환한다.
 */
export const sendFinalReport = async (req: SendFinalReportReq): Promise<string> => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/ut/ai-ref/report/final`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: {
      if_id: req.ifId ?? '',
      ifrc_type: 'final_report',
      report_id: req.reportId,
      report_nm: req.reportNm,
      report_matrix: {
        base: { utmId: req.baseUtmId },
        compared: { utmId: req.comparedUtmId },
        // 참조는 선택이므로 없으면 키를 만들지 않는다
        ...(req.referUtmId ? { refer: { utmId: req.referUtmId } } : {}),
      },
      user_content: req.userContent,
      ai_content: {
        mech: { sections: req.aiContent.mech },
        elec: { sections: req.aiContent.elec },
      },
    },
    // 초안 생성과 동일하게 AI 응답이 길다
    timeout: 240000,
  };
  const response: CommonResponse<string> = await callApi(request);
  if (response.successOrNot !== 'Y')
    throw new Error(String(response.data ?? '최종 리포트 AI 전송 실패'));
  return response.data ?? '';
};

/** OPIN_M 행들 → ai_content.sections[] (정렬순서대로 s1, s2 …) */
const toFinalSections = (rows: RptOpinion[]): FinalReportSection[] =>
  [...rows]
    .sort((a, b) => (a.sortOrd ?? 0) - (b.sortOrd ?? 0))
    .map((o, idx) => ({
      id: `s${idx + 1}`,
      title: o.anlsOpinTpNm || o.anlsOpinTpCd,
      body: o.aiAnsCtn || '',
    }));

/**
 * 최종 리포트 AI 호출 — 저장된 의견(OPIN_M)을 읽어 payload를 조립해 보낸다.
 *
 * **호출 시점은 "종합의견 저장" 직후**다(AnalysisOpinionSection). 그 시점이라야
 * user_content(사용자 검토 의견)가 실제로 채워져 있어서, "사용자 의견을 반영해 AI가
 * 최종본을 다시 쓴다"는 스펙 취지(ref/html/ut_matrix_rpt_demo.html 219줄)와 맞는다.
 *
 * targets를 넘기지 않으면 getRptTargets로 기준/비교대상/참조를 직접 조회한다.
 *
 * 응답을 기다리지 않는다(requestAiDraftOpinions와 동일). AI 실패가 의견 저장 자체를
 * 실패로 만들면 안 되므로 오류는 여기서 삼킨다.
 *
 * ⚠ 스펙의 ai_content.sections는 s1~s6 고정 6항목(설비 대수/공정별 변동/UT별 변동/
 *   검증 요약/우선 UT/조치 권고)으로 예시가 적혀 있으나, 현재 화면의 항목 수는
 *   공통코드 ANLS_OPIN_TP_CD에 따라 정해진다(기계 2건·전기 3건). 그래서 여기서는
 *   저장된 항목을 순서대로 s1..sN으로 담는다 — 6항목 고정이 맞다면 BE 확인 후 조정한다.
 */
export const requestFinalReport = (
  rptId: string,
  rptNm: string,
  targets?: { baseUtmId: string; comparedUtmId: string; referUtmId?: string }
) => {
  console.log('[최종 리포트] 전송 시작', { rptId, rptNm, targets });
  // BE가 응답을 파싱해 OPIN_M.AI_ANS_CTN에 반영하므로(BE-REQUEST ③),
  // 화면은 "반영 중" 표시를 띄우고 폴링으로 갱신된 내용을 가져온다.
  markStamp(AI_FINAL_KEY, rptId);
  // 호출한 쪽이 대상을 모르면(상세 화면의 "의견 저장" 경로) RPT_UTM_M에서 직접 읽는다
  const targetsOf = targets
    ? Promise.resolve(targets)
    : getRptTargets(rptId).then((rows) => ({
        baseUtmId: rows.find((r) => r.role === 'MST')?.utmId ?? '',
        comparedUtmId: rows.find((r) => r.role === 'TGT')?.utmId ?? '',
        referUtmId: rows.find((r) => r.role === 'REF')?.utmId,
      }));

  Promise.all([getRptOpinions(rptId), targetsOf])
    .then(([opinions, tgt]) => {
      const pickDomain = (cd: AtcDivsCd) => opinions.filter((o) => o.atcDivsCd === cd);
      const joinOpin = (rows: RptOpinion[]) =>
        rows
          .map((o) => o.opinCtn)
          .filter((v) => !!v)
          .join('\n');
      const mech = pickDomain('MECH');
      const elec = pickDomain('ELEC');

      if (!tgt.baseUtmId || !tgt.comparedUtmId) {
        // 대상을 못 찾으면 report_matrix가 비어 나가므로 아예 보내지 않는다
        throw new Error('기준/비교대상 UT Matrix를 확인할 수 없습니다.');
      }
      return sendFinalReport({
        reportId: rptId,
        reportNm: rptNm,
        baseUtmId: tgt.baseUtmId,
        comparedUtmId: tgt.comparedUtmId,
        referUtmId: tgt.referUtmId,
        userContent: { mech: joinOpin(mech), elec: joinOpin(elec) },
        aiContent: { mech: toFinalSections(mech), elec: toFinalSections(elec) },
      });
    })
    .then(() => console.log(`[최종 리포트] 전송 완료 (rptId=${rptId})`))
    .catch((e) => {
      console.error(`[최종 리포트] AI 전송 실패 (rptId=${rptId})`, e);
    });
};

/**
 * AI 초안 의견 생성 요청 — 리포트 생성 직후 **1회만** 접수한다.
 *
 * ⚠ **기계·전기를 따로 부르지 않는다.** AI 응답 한 건에 두 도메인이 함께 담겨 온다.
 *
 *   { "status": "success",
 *     "result": {
 *       "sections": { "mech": [{title, body} × 6], "elec": [{title, body} × 1] },
 *       "sketch_response": { "mech": {...}, "elec": {...} }
 *     } }
 *
 *   그래서 `reportDomain`을 바꿔 두 번 던질 필요가 없다. 전기 A-0004(전력 사용량 종합분석)도
 *   이 한 번의 응답에 `sections.elec`으로 들어온다.
 *   → pages/ut/ref/BE-REQUEST-elec-ai-power-analysis.md
 *
 * 응답 파싱·저장은 **BE 담당**이다. FE는 응답을 기다리지 않고 던져만 두고, 화면
 * (AnalysisOpinionSection)이 폴링으로 TB_EELMB_UTM_RPT_OPIN_M에서 읽는다. FE가 파싱해
 * 저장하면 브라우저를 닫는 순간 유실되고, saveRptOpinions가 전체 덮어쓰기라 다른 항목을
 * 지울 위험도 생긴다.
 *
 * AI 생성 실패가 리포트 생성 자체를 실패로 만들면 안 되므로 오류는 여기서 삼킨다.
 *
 * 리포트 생성 경로가 둘(UT Matrix Summary 화면 / 정합화 Report 목록 화면)이므로
 * 양쪽에서 함께 쓰도록 여기에 둔다.
 *
 * ⚠ 아직 BE가 AI 응답(약 180초)을 동기로 기다렸다가 반환하는 구조라, 이 요청은
 *   백그라운드에서 그만큼 열려 있고 그 사이 브라우저를 닫으면 초안이 유실된다.
 *   → pages/ut/ref/BE-REQUEST-ai-report-opinion-v2.md §2
 */
export const requestAiDraftOpinions = (rptId: string, rptNm: string, utmIds: string[]) => {
  const utmId = utmIds.join(',');
  // TODO(BE 연동 완료 후 제거) — 호출 여부 확인용 로그
  console.log('[AI 초안] 생성 요청', { rptId, utmId, rptNm });
  markAiDraftRequested(rptId);
  sendMatrixReport(utmId, 'mech', rptId, rptNm).catch((e) => {
    console.error(`[AI 초안] 생성 요청 실패 (rptId=${rptId})`, e);
  });
};

/**
 * AI 초안 생성 "진행 중" 판단 — 화면에서 "AI 분석 중…"을 띄우기 위한 임시 수단.
 *
 * BE 상태 조회 API(v3 §3 `/v1/ut/ai-ref/report/status`)가 아직 없어서,
 * 요청을 보낸 시점을 FE가 sessionStorage에 기록해두고 일정 시간 안이면 진행 중으로 본다.
 * AI 응답이 약 180초라 여유를 둬 5분으로 잡았다.
 *
 * ⚠ 한계 — 실제 성공/실패를 알 수 없고, 다른 브라우저·탭에서 만든 리포트는 알 수 없다.
 *   상태 API가 생기면 이 블록(markAiDraftRequested/isAiDraftPending)은 통째로 대체한다.
 */
const AI_DRAFT_KEY = 'utAiDraftRequestedAt';
/** 최종 리포트(/report/final) 요청 시각 — 초안과 별도로 관리한다 */
const AI_FINAL_KEY = 'utAiFinalRequestedAt';
const AI_TIMEOUT_MS = 5 * 60 * 1000;

const readStampMap = (key: string): Record<string, number> => {
  try {
    return JSON.parse(sessionStorage.getItem(key) || '{}');
  } catch {
    return {};
  }
};

const markStamp = (key: string, rptId: string) => {
  try {
    const map = readStampMap(key);
    map[rptId] = Date.now();
    sessionStorage.setItem(key, JSON.stringify(map));
  } catch {
    // 저장에 실패해도 "생성 중" 표시만 안 될 뿐 기능에는 영향이 없다
  }
};

const clearStamp = (key: string, rptId: string) => {
  try {
    const map = readStampMap(key);
    delete map[rptId];
    sessionStorage.setItem(key, JSON.stringify(map));
  } catch {
    // 무시 — 시간이 지나면 어차피 만료된다
  }
};

const isStampFresh = (key: string, rptId: string): boolean => {
  const at = readStampMap(key)[rptId];
  return typeof at === 'number' && Date.now() - at < AI_TIMEOUT_MS;
};

const markAiDraftRequested = (rptId: string) => markStamp(AI_DRAFT_KEY, rptId);

/** 이 리포트의 AI 초안 생성이 아직 진행 중일 수 있는지 */
export const isAiDraftPending = (rptId: string): boolean => isStampFresh(AI_DRAFT_KEY, rptId);

/** 최종 리포트 AI 반영이 아직 진행 중일 수 있는지 */
export const isFinalReportPending = (rptId: string): boolean => isStampFresh(AI_FINAL_KEY, rptId);

/** 내용이 실제로 갱신된 걸 확인했을 때 진행 표시를 끈다 */
export const clearFinalReportPending = (rptId: string) => clearStamp(AI_FINAL_KEY, rptId);

// ─── "분석 Report" 탭 (TB_EELMB_UTM_RPT_SUM_M) ──────────────────────────
//
// ⚠ 아래 4개 엔드포인트는 아직 BE 미개발이다(2026-09-09 기준).
//   요청 스펙은 pages/ut/ref/BE-REQUEST-rpt-sum.md 참고.
//   미개발 상태에서는 호출이 실패하고 AnalysisReportPanel이 안내문구로 degrade된다.

/** BE 응답 키가 camelCase일 수도, Map 기반 대문자 스네이크일 수도 있어 둘 다 받는다 */
const pick = (raw: any, camel: string, upper: string): any => raw?.[camel] ?? raw?.[upper];

const toNum = (v: any): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

const toRptUtmTarget = (raw: any): RptUtmTarget => ({
  utmId: String(pick(raw, 'utmId', 'UTM_ID') ?? ''),
  sortOrd: Number(pick(raw, 'sortOrd', 'SORT_ORD') ?? 0),
  role: (pick(raw, 'role', 'MST_TGT_TP_CD') ?? 'MST') as MstTgtTpCd,
  utmNo: String(pick(raw, 'utmNo', 'UTM_NO') ?? ''),
  utmNm: String(pick(raw, 'utmNm', 'UTM_NM') ?? ''),
  verNo: String(pick(raw, 'verNo', 'VER_NO') ?? ''),
  // 매퍼(selectRptUtmTargets)가 statusNm 별칭으로 내려주는 경우도 함께 받는다
  utmStatNm: String(
    pick(raw, 'utmStatNm', 'UTM_STAT_NM') ?? pick(raw, 'statusNm', 'STATUS_NM') ?? ''
  ),
  prdnLnNm: String(pick(raw, 'prdnLnNm', 'PRDN_LN_NM') ?? ''),
});

/** 대상 UT Matrix 2~3건(역할 포함) — 분석 Report 표의 행이 된다 */
export const getRptTargets = async (rptId: string): Promise<RptUtmTarget[]> => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/ut/getRptTargets`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ rptId }),
  };
  const response: CommonResponse<any[]> = await callApi(request);
  if (response.successOrNot !== 'Y') throw new Error('대상 UT Matrix 조회 실패');
  return (response.data ?? []).map(toRptUtmTarget).sort((a, b) => a.sortOrd - b.sortOrd);
};

/**
 * SUM_M 응답 정규화 — BE가 역할별로 피벗해 내려주는 형태(values 맵)와, 매퍼
 * selectRptSumRows처럼 역할당 1행씩 평평하게 내려주는 형태를 모두 받는다.
 * 평평한 경우 (ATC_DIVS_CD + UTM_TP_CD)로 묶어 role → MSR_NVL 맵으로 피벗한다.
 */
export const toRptSumItems = (rows: any[]): RptSumItem[] => {
  const merged = new Map<string, RptSumItem>();

  (rows ?? []).forEach((raw) => {
    const atcDivsCd = (pick(raw, 'atcDivsCd', 'ATC_DIVS_CD') ?? 'MECH') as AtcDivsCdSum;
    const utmTpCd = String(pick(raw, 'utmTpCd', 'UTM_TP_CD') ?? '');
    if (!utmTpCd) return;
    const key = `${atcDivsCd}|${utmTpCd}`;

    if (!merged.has(key)) {
      // 표시명 — 전기는 공정명(ELEC_NM), 기계는 UT_TYPES 라벨로 보완한다
      const utType = findUtType(utmTpCd);
      const utmTpNm =
        pick(raw, 'utmTpNm', 'UTM_TP_NM') ??
        pick(raw, 'elecNm', 'ELEC_NM') ??
        utType?.label ??
        utmTpCd;
      merged.set(key, {
        atcDivsCd,
        utmTpCd,
        utmTpNm: String(utmTpNm),
        msrUnitCd: String(
          pick(raw, 'msrUnitCd', 'MSR_UNIT_CD') ?? (atcDivsCd === 'ELEC' ? 'MW' : utUnitOf(utmTpCd))
        ),
        rpotTgtYn: pick(raw, 'rpotTgtYn', 'RPOT_TGT_YN') === 'N' ? 'N' : 'Y',
        values: {},
        shrtQty: null,
        infcRvwCtn: String(pick(raw, 'infcRvwCtn', 'INFC_RVW_CTN') ?? ''),
        rvwCtn: String(pick(raw, 'rvwCtn', 'RVW_CTN') ?? ''),
        nextPlanCtn: String(pick(raw, 'nextPlanCtn', 'NEXT_PLAN_CTN') ?? ''),
      });
    }
    const item = merged.get(key)!;

    // 이미 피벗된 응답이면 values를 그대로 흡수한다
    const pivoted = raw?.values;
    if (pivoted && typeof pivoted === 'object') {
      Object.entries(pivoted).forEach(([role, v]) => {
        const n = toNum(v);
        if (n !== null) item.values[role as MstTgtTpCd] = n;
      });
    } else {
      const role = pick(raw, 'role', 'ROLE') as MstTgtTpCd | undefined;
      const msrNvl = toNum(pick(raw, 'msrNvl', 'MSR_NVL'));
      if (role && msrNvl !== null) item.values[role] = msrNvl;
    }

    // 부족량·검토 내역은 TGT 행에만 저장된다. 위 초기화는 항목별 최초 1회만 실행되므로,
    // MST 행이 먼저 오면 빈 값이 그대로 남고 뒤따르는 TGT 행의 내용이 반영되지 않는다.
    // 그래서 이 세 칸은 값이 있는 행을 만날 때마다 덮어쓴다.
    const shrtQty = toNum(pick(raw, 'shrtQty', 'SHRT_QTY'));
    if (shrtQty !== null) item.shrtQty = shrtQty;
    item.infcRvwCtn = String(pick(raw, 'infcRvwCtn', 'INFC_RVW_CTN') ?? '') || item.infcRvwCtn;
    item.rvwCtn = String(pick(raw, 'rvwCtn', 'RVW_CTN') ?? '') || item.rvwCtn;
    item.nextPlanCtn = String(pick(raw, 'nextPlanCtn', 'NEXT_PLAN_CTN') ?? '') || item.nextPlanCtn;
  });

  // target이 es5인데 downlevelIteration이 꺼져 있어 Map 이터레이터는 스프레드로 못 편다(TS2802)
  return Array.from(merged.values());
};

/** "분석 Report" 탭 UT 항목 목록 (SUM_M) */
export const getRptSumRows = async (rptId: string): Promise<RptSumItem[]> => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/ut/getRptSumRows`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ rptId }),
  };
  const response: CommonResponse<any[]> = await callApi(request);
  if (response.successOrNot !== 'Y') throw new Error('UT Matrix 용량 정합화 조회 실패');
  return toRptSumItems(response.data ?? []);
};

/** 검토 내역(투자비영향/내역/향후계획) 저장 — 표시 중인 항목 전체를 일괄 저장한다 */
export interface RptSumReviewRow {
  atcDivsCd: AtcDivsCdSum;
  utmTpCd: string;
  infcRvwCtn: string;
  rvwCtn: string;
  nextPlanCtn: string;
}

export const saveRptSumReview = async (
  rptId: string,
  tgtUtmId: string,
  rows: RptSumReviewRow[],
  userId: string
): Promise<void> => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/ut/saveRptSumReview`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: { rptId, tgtUtmId, userId, rows },
  };
  const response: CommonResponse<any> = await callApi(request);
  if (response.successOrNot !== 'Y')
    throw new Error(String(response.data ?? '검토 내역 저장 실패'));
};

/**
 * UT 항목 표시여부 토글 (RPOT_TGT_YN) — 이 UT 항목의 기준/비교대상/참조 행에 모두 반영된다.
 * 화면 필터 버튼이 곧 DB 값이라 새로고침해도 유지된다.
 */
export const saveRptSumTargetYn = async (
  rptId: string,
  atcDivsCd: AtcDivsCdSum,
  utmTpCd: string,
  rpotTgtYn: 'Y' | 'N',
  userId: string
): Promise<void> => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/ut/saveRptSumTargetYn`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: { rptId, atcDivsCd, utmTpCd, rpotTgtYn, userId },
  };
  const response: CommonResponse<any> = await callApi(request);
  if (response.successOrNot !== 'Y')
    throw new Error(String(response.data ?? '표시 설정 변경 실패'));
};

/** 기계 리포트 상세 조회 — rptId로 마스터/대상/분석결과 반환 */
export const getReportDetail = async (rptId: string) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/ut/getReportDetail`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ rptId }),
  };
  const response: CommonResponse<any> = await callApi(request);
  if (response.successOrNot !== 'Y') throw new Error('리포트 상세 조회 실패');
  return response.data;
};

/**
 * 리포트 삭제 - 관련 테이블 데이터 전부 삭제
 */
export const deleteReportList = async (deleteData: UtMatrixComparisonReportList[]) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/ut/deleteReportList`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: deleteData,
  };
  const response: CommonResponse<any> = await callApi(request);
  return response;
};
